import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import type { FormSchema } from '../types';
import { useFormContext } from '../context/FormContext';
import { StepIndicatorSimple } from './StepIndicatorSimple';
import { StepIndicatorEnhanced } from './StepIndicatorEnhanced';
import { StepView } from './StepView';
import { Recaptcha } from './Recaptcha';
import { Loader } from './Loader';
import { SubmitErrorModal } from './SubmitErrorModal';
import { useStepUrl } from '../hooks/useStepUrl';
import { formUrl } from '../services/formUrls';

export interface StepFormProps {
  schema: FormSchema;
  children?: ReactNode;
  customStepComponents?: Record<string, ComponentType<{ stepSchema: FormSchema['steps'][number] }>>;
}

export function StepForm({ schema, children, customStepComponents = {} }: StepFormProps) {
  const {
    formService,
    stateService,
    dataSummarizer,
    recaptchaService,
    scrollService,
    displayService,
    settings,
    forceUpdate,
  } = useFormContext();

  useStepUrl();

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [submitMessage] = useState<string>('');
  const [recaptchaError, setRecaptchaError] = useState<boolean>(false);
  const disableRecaptcha = settings.disableRecaptcha ?? false;

  /**
   * The id grecaptcha assigned to the live widget. Leaving the final step (via 'Edit this
   * section') unmounts the widget, and coming back renders a fresh one under a new id, so
   * this has to follow the widget rather than assume the first one.
   */
  const submitRecaptchaId = useRef<number | null>(null);

  const handleRecaptchaRendered = useCallback<(widgetId: number) => void>((widgetId) => {
    submitRecaptchaId.current = widgetId;
  }, []);

  useEffect(() => {
    if (stateService.loadedSavedData) {
      const timer = setTimeout(() => {
        formService.setDefaultActiveStep(displayService);
        forceUpdate();
        setTimeout(() => scrollService.stepScrollToTop(), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [stateService.loadedSavedData, formService, displayService, scrollService, forceUpdate]);

  useEffect(() => {
    if (settings.submitResponse?.success) {
      stateService.formSubmitted = true;
      stateService.showForm = false;
      forceUpdate();
    }
  }, [settings.submitResponse, stateService, forceUpdate]);

  if (!stateService.showForm) {
    return null;
  }

  const activeStep = formService.getActiveStep();
  const formComplete = activeStep ? formService.isFinalStep(activeStep) : false;

  const recaptchaVerified = () => {
    if (disableRecaptcha) {
      return true;
    }

    return (
      submitRecaptchaId.current !== null &&
      recaptchaService.validateResponse(submitRecaptchaId.current)
    );
  };

  const enableSubmit = () => {
    const form = formService.getFormData();
    const declared = form.declaration !== null ? form.declaration.completed() : true;

    const paymentTermsOk =
      settings.requirePaymentTerms === true
        ? form.acceptPaymentTermsAndConditions
        : true;

    return declared && recaptchaVerified() && paymentTermsOk;
  };

  const getSubmitButtonLabel = () => settings.submitButtonLabel || 'Submit and pay';
  const getActionUrl = () => formUrl(settings.apiBaseUrl, 'submit');

  const submit = () => {
    const form = formService.getFormData();
    if (form.declaration) {
      form.declaration.validate();
      forceUpdate();
    }

    // Surface why submitting is blocked, rather than leaving the button looking dead
    setRecaptchaError(!recaptchaVerified());

    if (!enableSubmit()) {
      return;
    }

    setSubmitLoading(true);

    const dataToSubmit = dataSummarizer.summarize(form) as Record<string, unknown>;
    dataToSubmit.recaptchaResponse =
      submitRecaptchaId.current === null
        ? ''
        : recaptchaService.getResponse(submitRecaptchaId.current);

    const dataInput = document.getElementById('form-submit-data') as HTMLInputElement | null;
    const formEl = document.getElementById('form-submit') as HTMLFormElement | null;

    if (dataInput && formEl) {
      dataInput.value = JSON.stringify(dataToSubmit);
      formEl.submit();
    }
  };

  const stepIndicatorStyle = settings.stepIndicatorStyle ?? 'simple';

  return (
    <div id="stepform">
      {submitLoading && <Loader />}
      <SubmitErrorModal />

      <div>
        {submitMessage && <div className="stepform-submit-error">{submitMessage}</div>}

        {stepIndicatorStyle !== 'enhanced' ? (
          <StepIndicatorSimple />
        ) : (
          <StepIndicatorEnhanced />
        )}

        <div className="stepform-form-section form-holder">
          <div className="inner">
            <div className="stepform-q-a-area">
              {schema.steps.map((stepSchema) => {
                const CustomStep = stepSchema.component
                  ? customStepComponents[stepSchema.component]
                  : null;

                if (CustomStep) {
                  return <CustomStep key={stepSchema.id} stepSchema={stepSchema} />;
                }

                return <StepView key={stepSchema.id} stepSchema={stepSchema} />;
              })}

              {children}

              {formComplete && (
                <div>
                  <Recaptcha
                    onWidgetRendered={handleRecaptchaRendered}
                    showError={recaptchaError}
                  />

                  <hr className="pull-right" />

                  <form id="form-submit" method="post" action={getActionUrl()}>
                    <input
                      type="hidden"
                      style={{ display: 'none' }}
                      id="form-submit-data"
                      name="data"
                      value=""
                    />
                  </form>

                  <div className="submit-wrapper">
                    <div className="buttons">
                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            formService.setPreviousActiveStep();
                            forceUpdate();
                            scrollService.stepScrollToTop();
                          }}
                          className="button bordered"
                        >
                          Back
                        </button>
                      </div>
                      <div>
                        <button type="button" onClick={submit} className="button">
                          {getSubmitButtonLabel()}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="column"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
